import axios, { AxiosError } from 'axios';
import { getFingerprint } from 'src/components/common/Helpers/Fingerprint';

// Define the API base URL - replace with your actual backend URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface ChatRequestParams {
  message: string;
  conversation_id?: string;
  name: string;
  level: string;
  subject: string;
  is_public: boolean;
  chat_mode: 'parent' | 'child';
  is_new_doc_uploaded?: boolean; // Add flag to indicate if file was uploaded
}

interface ChatResponse {
  response: string;
  conversation_id: string;
  is_complete: boolean;
  should_open_Email: boolean;
}

interface SessionResponse {
  response: string;
  conversation_id: string;
}

// New interface for session creation request
interface SessionRequestParams {
  name: string;
  level: string;
  subject: string;
  is_public: boolean;
  chat_mode: 'parent' | 'child';
}

interface FeedbackRequestParams {
  conversation_id: string;
  message: string;
  name: string;
  email: string;
}

export interface FeedbackApiResponse {
  success: boolean;
  message: string;
}

interface FeedbackRequestParams {
  conversation_id: string;
  message: string;
  name: string;
  email: string;
}

export interface FeedbackApiResponse {
  success: boolean;
  message: string;
}

interface PubSubParams {
  conversation_id: string;
}

interface PubSubTokenResponse {
  token: string;
}

// Error response interface
interface ApiErrorResponse {
  message?: string;
  detail?: string;
  error?: string;
}

// Helper function to handle API errors
const handleApiError = (error: unknown): never => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    // Handle rate limiting (429 Too Many Requests)
    if (axiosError.response?.status === 429) {
      const errorMessage =
        axiosError.response?.data?.detail ||
        'You have made too many requests. Please wait a moment before trying again.';
      throw new Error(errorMessage);
    }

    // Handle other common status codes
    if (axiosError.response?.status === 400) {
      const errorMessage =
        axiosError.response.data?.message ||
        axiosError.response.data?.detail ||
        axiosError.response.data?.error ||
        'Invalid request. Please check your input.';
      throw new Error(errorMessage);
    }

    if (
      axiosError.response?.status === 401 ||
      axiosError.response?.status === 403
    ) {
      throw new Error('You are not authorized to perform this action.');
    }

    if (axiosError.response?.status === 404) {
      throw new Error('The requested resource was not found.');
    }

    if (axiosError.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    }

    // Extract error message from response if available
    const errorMessage =
      axiosError.response?.data?.message ||
      axiosError.response?.data?.detail ||
      axiosError.response?.data?.error ||
      axiosError.message ||
      'An error occurred while processing your request.';

    throw new Error(errorMessage);
  }

  // For non-Axios errors
  throw error instanceof Error
    ? error
    : new Error('An unexpected error occurred.');
};

// Function to create a new public chat session
export const createPublicChatSession = async (
  params: SessionRequestParams
): Promise<SessionResponse> => {
  const endpoint = `${API_BASE_URL}/public/chat/session`;

  try {
    // Get fingerprint for rate limiting
    const fingerprint = await getFingerprint();

    // Note: The backend has a typo in the schema - 'leve' instead of 'level'
    const response = await axios.post(
      endpoint,
      {
        name: params.name,
        level: params.level, // Using 'leve' to match backend schema
        subject: params.subject,
        is_public: params.is_public,
        chat_mode: params.chat_mode,
      },
      {
        headers: {
          'X-Fingerprint': fingerprint,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating public chat session:', error);
    return handleApiError(error);
  }
};

export const getPubSubToken = async (
  conversationId: string
): Promise<PubSubTokenResponse> => {
  const endpoint = `${API_BASE_URL}/users/pubsub-token-public/${conversationId}`;

  try {
    // Get fingerprint for rate limiting
    const fingerprint = await getFingerprint();

    const response = await axios.get<PubSubTokenResponse>(endpoint, {
      headers: {
        'X-Fingerprint': fingerprint,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Web PubSub token:', error);
    return handleApiError(error);
  }
};

// Function to get Azure Storage URL for file upload
export const getAzureStorageUrl = async (
  fileName: string,
  conversation_id: string
): Promise<string> => {
  try {
    // Get fingerprint for rate limiting
    const fingerprint = await getFingerprint();

    const endpoint = `${API_BASE_URL}/users/documents/get-azure-storage-upload-url-public/${conversation_id}/${fileName}`;
    const response = await axios.get(endpoint, {
      headers: {
        'X-Fingerprint': fingerprint,
      },
    });

    if (response.data && response.data.upload_url) {
      return response.data.upload_url;
    } else {
      throw new Error('Failed to get Azure Storage URL');
    }
  } catch (error) {
    console.error('Error getting Azure Storage URL:', error);
    return handleApiError(error);
  }
};

// Function to upload file to Azure Storage
export const uploadFileToAzure = async (
  file: File,
  conversation_id: string
): Promise<boolean> => {
  try {
    // Get the Azure Storage URL first
    const azureUrl = await getAzureStorageUrl(file.name, conversation_id);

    // Upload the file to Azure Storage
    const response = await axios.put(azureUrl, file, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type,
        'x-ms-meta-chat_session_id': conversation_id,
      },
    });

    return response.status >= 200 && response.status < 300;
  } catch (error) {
    console.error('Error uploading file to Azure:', error);

    // For upload errors, we'll return false instead of throwing
    // so the UI can handle it appropriately
    if (error instanceof Error) {
      throw error; // Propagate the error with its message
    }

    throw new Error('Failed to upload file. Please try again.');
  }
};

// Function to send a message to the public chat API
export const sendPublicChatMessage = async (
  params: ChatRequestParams
): Promise<ChatResponse> => {
  const endpoint = `${API_BASE_URL}/public/chat/send-msg`;

  try {
    // Get fingerprint for rate limiting
    const fingerprint = await getFingerprint();

    // Send regular JSON request with fingerprint in headers
    const response = await axios.post(
      endpoint,
      {
        message: params.message,
        conversation_id: params.conversation_id,
        name: params.name,
        level: params.level,
        subject: params.subject,
        is_public: params.is_public,
        chat_mode: params.chat_mode,
        is_new_doc_uploaded: params.is_new_doc_uploaded || false,
      },
      {
        headers: {
          'X-Fingerprint': fingerprint,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error sending chat message:', error);
    return handleApiError(error);
  }
};

export const generateSuggestedQuestion = async (
  zep_session_id: string
): Promise<boolean> => {
  const endpoint = `${API_BASE_URL}/public/chat/suggested_question`;

  try {
    // Get fingerprint for rate limiting
    const fingerprint = await getFingerprint();

    // Send regular JSON request with fingerprint in headers
    const response = await axios.post(
      endpoint,
      {
        zep_session_id: zep_session_id,
      },
      {
        headers: {
          'X-Fingerprint': fingerprint,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error sending chat message:', error);
    return false;
  }
};

export const sendPublicChatFeedback = async (
  params: FeedbackRequestParams
): Promise<FeedbackApiResponse> => {
  const endpoint = `${API_BASE_URL}/public/chat/feedback`;

  try {
    // Get fingerprint for rate limiting
    const fingerprint = await getFingerprint();

    // Send regular JSON request with fingerprint in headers
    const response = await axios.post(
      endpoint,
      {
        conversation_id: params.conversation_id,
        message: params.message,
        name: params.name,
        email: params.email,
      },
      {
        headers: {
          'X-Fingerprint': fingerprint,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 429) {
        // Handle rate limit error
        return {
          success: false,
          message:
            "You're sending feedback too frequently. Please try again later.",
        };
      }

      return {
        success: false,
        message:
          error.response?.data?.message ||
          'An error occurred while sending feedback.',
      };
    }
    console.error('Error sending chat message:', error);
    return { success: false, message: 'Failed to send feedback.' };
  }
};

export const sendSuggestedQuestionEmail = async (
  zep_session_id: string,
  email: string,
  name: string
): Promise<boolean> => {
  const endpoint = `${API_BASE_URL}/public/chat/send_email`;

  try {
    // Get fingerprint for rate limiting
    const fingerprint = await getFingerprint();

    // Send regular JSON request with fingerprint in headers
    const response = await axios.post(
      endpoint,
      {
        zep_session_id: zep_session_id,
        email: email,
        name: name,
      },
      {
        headers: {
          'X-Fingerprint': fingerprint,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error sending chat message:', error);
    return false;
  }
};

type Level = {
  id: number;
  name: string;
};

type Subject = {
  id: number;
  name: string;
};

export const getLevels = async (): Promise<Level[] | boolean> => {
  const endpoint = `${API_BASE_URL}/public/chat/grade-levels`;

  try {
    const response = await axios.get(endpoint);

    return response.data;
  } catch (error) {
    console.error('Error sending chat message:', error);
    return false;
  }
};

export const getSubjects = async (
  level: string
): Promise<Subject[] | boolean> => {
  const endpoint = `${API_BASE_URL}/public/chat/subjects?grade_level_id=${level}`;

  try {
    const response = await axios.get(endpoint);

    return response.data;
  } catch (error) {
    console.error('Error sending chat message:', error);
    return false;
  }
};
